<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  listAds,
  createAd,
  updateAd,
  deleteAd,
  uploadAdImage,
  adImageAbsUrl,
  type AdRow,
  type AdPlacement,
  type AdLinkType,
  type AdPayload,
} from '../api';

const PLACEMENTS: { value: AdPlacement; label: string }[] = [
  { value: 'HOME', label: '首页' },
  { value: 'PLAZA', label: '广场圈' },
  { value: 'MFRIENDS', label: 'M友名录' },
  { value: 'PROFILE', label: '个人中心' },
];
const LINK_TYPES: { value: AdLinkType; label: string }[] = [
  { value: 'NONE', label: '纯展示(不可点)' },
  { value: 'MINIPROGRAM', label: '小程序内页' },
  { value: 'H5', label: '外部链接(H5)' },
];

const placementLabel = (v: string) => PLACEMENTS.find((p) => p.value === v)?.label ?? v;
const linkLabel = (v: string) => LINK_TYPES.find((l) => l.value === v)?.label ?? v;

const filter = ref<AdPlacement | ''>('');
const loading = ref(false);
const list = ref<AdRow[]>([]);

async function reload() {
  loading.value = true;
  try {
    const r = await listAds(filter.value || undefined);
    list.value = r.items;
  } catch (e) {
    ElMessage.error(normalize(e));
  } finally {
    loading.value = false;
  }
}

// —— 新增/编辑弹窗 ——
const dialogVisible = ref(false);
const editingId = ref<string | null>(null);
const uploading = ref(false);
const saving = ref(false);
const form = reactive<AdPayload>({
  placement: 'HOME',
  title: '',
  imageUrl: '',
  imageKey: '',
  linkType: 'NONE',
  linkValue: '',
  sort: 0,
  active: true,
});

function resetForm(placement: AdPlacement = 'HOME') {
  editingId.value = null;
  Object.assign(form, {
    placement,
    title: '',
    imageUrl: '',
    imageKey: '',
    linkType: 'NONE',
    linkValue: '',
    sort: 0,
    active: true,
  });
}

function openCreate() {
  resetForm((filter.value || 'HOME') as AdPlacement);
  dialogVisible.value = true;
}

function openEdit(row: AdRow) {
  editingId.value = row.id;
  Object.assign(form, {
    placement: row.placement,
    title: row.title ?? '',
    imageUrl: row.imageUrl,
    imageKey: row.imageKey,
    linkType: row.linkType,
    linkValue: row.linkValue ?? '',
    sort: row.sort,
    active: row.active,
  });
  dialogVisible.value = true;
}

// el-upload 前置:读文件为 base64 dataURL 后走后端 upload,拿回 {url,key}
async function onPickImage(file: File) {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    ElMessage.error('仅支持 jpg/png/webp');
    return false;
  }
  if (file.size > 2 * 1024 * 1024) {
    ElMessage.error('图片不超过 2MB');
    return false;
  }
  uploading.value = true;
  try {
    const dataUrl = await readAsDataUrl(file);
    const r = await uploadAdImage(dataUrl, file.type, file.name);
    form.imageUrl = r.url;
    form.imageKey = r.key;
    ElMessage.success('图片已上传');
  } catch (e) {
    ElMessage.error(normalize(e));
  } finally {
    uploading.value = false;
  }
  return false; // 阻止 el-upload 默认上传
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result as string);
    fr.onerror = () => reject(new Error('读取图片失败'));
    fr.readAsDataURL(file);
  });
}

async function onSubmit() {
  if (!form.imageUrl || !form.imageKey) {
    ElMessage.error('请先上传广告图');
    return;
  }
  if (form.linkType !== 'NONE' && !(form.linkValue || '').trim()) {
    ElMessage.error('所选跳转类型需填写跳转地址');
    return;
  }
  saving.value = true;
  try {
    const payload: AdPayload = {
      placement: form.placement,
      title: (form.title || '').trim() || null,
      imageUrl: form.imageUrl,
      imageKey: form.imageKey,
      linkType: form.linkType,
      linkValue: form.linkType === 'NONE' ? null : (form.linkValue || '').trim(),
      sort: Number(form.sort) || 0,
      active: form.active,
    };
    if (editingId.value) {
      await updateAd(editingId.value, payload);
      ElMessage.success('已更新');
    } else {
      await createAd(payload);
      ElMessage.success('已新增');
    }
    dialogVisible.value = false;
    reload();
  } catch (e) {
    ElMessage.error(normalize(e));
  } finally {
    saving.value = false;
  }
}

async function onToggleActive(row: AdRow) {
  try {
    await updateAd(row.id, { active: !row.active });
    ElMessage.success(row.active ? '已停用' : '已启用');
    reload();
  } catch (e) {
    ElMessage.error(normalize(e));
  }
}

async function onDelete(row: AdRow) {
  try {
    await ElMessageBox.confirm(`确认删除该广告位?删除后小程序对应位置将不再展示。`, '删除广告位', {
      type: 'warning',
      confirmButtonText: '删除',
      cancelButtonText: '取消',
    });
    await deleteAd(row.id);
    ElMessage.success('已删除');
    reload();
  } catch (e) {
    if ((e as { message?: string })?.message === 'cancel' || e === 'cancel') return;
    ElMessage.error(normalize(e));
  }
}

function normalize(e: unknown): string {
  const err = e as { message?: string; code?: string };
  return err?.message || err?.code || '未知错误';
}
function formatTime(s: string | null): string {
  return s ? new Date(s).toLocaleString('zh-CN') : '-';
}

onMounted(reload);
</script>

<template>
  <div class="ads-pane">
    <div class="toolbar">
      <el-select v-model="filter" placeholder="全部页面" clearable style="width: 180px" @change="reload">
        <el-option v-for="p in PLACEMENTS" :key="p.value" :label="p.label" :value="p.value" />
      </el-select>
      <el-button @click="reload" style="margin-left: 8px">刷新</el-button>
      <el-button type="primary" @click="openCreate" style="margin-left: auto">＋ 新增广告位</el-button>
    </div>

    <el-alert
      type="info"
      :closable="false"
      title="每个页面的广告位分开配置;未添加或全部停用时,小程序对应位置不显示广告。同页多条自动轮播。"
      style="margin-bottom: 12px"
    />

    <el-table :data="list" v-loading="loading" stripe>
      <el-table-column label="预览" width="140">
        <template #default="{ row }">
          <el-image
            :src="adImageAbsUrl(row.imageUrl)"
            fit="cover"
            style="width: 112px; height: 56px; border-radius: 6px"
            :preview-src-list="[adImageAbsUrl(row.imageUrl)]"
            preview-teleported
          />
        </template>
      </el-table-column>
      <el-table-column label="页面" width="110">
        <template #default="{ row }">
          <el-tag size="small">{{ placementLabel(row.placement) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="标题" min-width="140">
        <template #default="{ row }">{{ row.title || '-' }}</template>
      </el-table-column>
      <el-table-column label="跳转" min-width="200">
        <template #default="{ row }">
          <div>{{ linkLabel(row.linkType) }}</div>
          <span class="muted" v-if="row.linkValue">{{ row.linkValue }}</span>
        </template>
      </el-table-column>
      <el-table-column label="排序" width="70">
        <template #default="{ row }">{{ row.sort }}</template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template #default="{ row }">
          <el-tag :type="row.active ? 'success' : 'info'" size="small">
            {{ row.active ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="创建" width="160">
        <template #default="{ row }">{{ formatTime(row.createdAt) }}</template>
      </el-table-column>
      <el-table-column label="操作" fixed="right" width="210">
        <template #default="{ row }">
          <el-button size="small" @click="openEdit(row)">编辑</el-button>
          <el-button size="small" :type="row.active ? 'warning' : 'success'" @click="onToggleActive(row)">
            {{ row.active ? '停用' : '启用' }}
          </el-button>
          <el-button size="small" type="danger" @click="onDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      v-model="dialogVisible"
      :title="editingId ? '编辑广告位' : '新增广告位'"
      width="560px"
    >
      <el-form label-width="88px">
        <el-form-item label="投放页面">
          <el-select v-model="form.placement" style="width: 100%">
            <el-option v-for="p in PLACEMENTS" :key="p.value" :label="p.label" :value="p.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="广告图">
          <div class="upload-box">
            <el-image
              v-if="form.imageUrl"
              :src="adImageAbsUrl(form.imageUrl)"
              fit="cover"
              style="width: 240px; height: 120px; border-radius: 8px; margin-bottom: 8px"
            />
            <el-upload
              :show-file-list="false"
              :before-upload="onPickImage"
              accept="image/jpeg,image/png,image/webp"
            >
              <el-button :loading="uploading">{{ form.imageUrl ? '重新上传' : '上传图片' }}</el-button>
            </el-upload>
            <div class="muted">建议 690×345 或同比例;jpg/png/webp,≤2MB</div>
          </div>
        </el-form-item>
        <el-form-item label="标题备注">
          <el-input v-model="form.title" maxlength="64" placeholder="运营备注 / 无障碍描述(选填)" />
        </el-form-item>
        <el-form-item label="跳转类型">
          <el-select v-model="form.linkType" style="width: 100%">
            <el-option v-for="l in LINK_TYPES" :key="l.value" :label="l.label" :value="l.value" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="form.linkType !== 'NONE'" label="跳转地址">
          <el-input
            v-model="form.linkValue"
            :placeholder="form.linkType === 'H5' ? 'https:// 开头的外链' : '小程序内页路径,如 /pages/plaza/index'"
          />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort" :min="0" :max="999" />
          <span class="muted" style="margin-left: 8px">小值在前(同页轮播顺序)</span>
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.active" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="onSubmit">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped>
.ads-pane { padding: 4px 0; }
.toolbar { display: flex; align-items: center; margin-bottom: 12px; }
.muted { color: #86868b; font-size: 12px; }
.upload-box { display: flex; flex-direction: column; align-items: flex-start; }
</style>
